import type { ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAccount } from '../../lib/account/useAccount';
import { useAccountingAccess } from '../../hooks/useAccountingAccess';
import { useInvoicingVisible } from '../../hooks/useInvoicingVisible';
import { usePreloadSidebarRoutes } from '../../hooks/usePreloadSidebarRoutes';
import { usePrefetchRouteData } from '../../hooks/usePrefetchRouteData';
import { sidebarRouteLoaders } from '../../lib/route-loaders';
import { prefetchChunk } from '../../lib/stale-chunk';
import { BillLogo } from '../ui/BillLogo';
import { Skeleton } from '../ui/Skeleton';
import { CalendarIcon } from '../ui/CalendarIcon';
import { ClockIcon } from '../ui/ClockIcon';
import { TimerWidget } from '../timer/TimerWidget';
import { firmOnlySurfacesVisible, visibleNavItems, type NavItemId } from './nav-items';

interface SidebarProps {
  className?: string;
  onClose?: () => void;
}

export function Sidebar({ className = '', onClose }: SidebarProps) {
  const accountingVisible = useAccountingAccess() === 'granted';
  const { account, administeredBy, isLoading } = useAccount();
  const invoicingVisible = useInvoicingVisible();
  const navInputs = { accountingVisible, administered: !!administeredBy, accountLoading: isLoading, invoicingVisible };
  const navItems = visibleNavItems(navInputs);
  const location = useLocation();
  usePreloadSidebarRoutes(navItems.map((item) => item.to));

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isSettingsPage = location.pathname === '/settings';

  const handleNavClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    // As the mobile drawer this aside is `fixed inset-y-0`, but an explicit
    // height beats the bottom inset, so it needs h-dvh, not h-screen: vh
    // overshoots under the mobile browser toolbar and takes the timer widget at
    // the foot off screen (see routes/_layout.tsx). The desktop instance sits
    // beside the content and fills the viewport instead.
    <aside
      className={`flex ${onClose ? 'h-dvh' : 'h-full'} w-64 shrink-0 flex-col border-r border-border/70 bg-bg-primary ${className}`}
    >
      <div className="flex h-14 shrink-0 items-center px-5">
        <Link to="/dashboard" className="text-2xl text-primary no-underline" aria-label="Bill, dashboard" onClick={handleNavClick}>
          <BillLogo />
        </Link>
      </div>

      {/* Practice name - who is signed in, and as what, lives in the account menu */}
      <div className="px-5 pb-2 pt-3">
        {account?.name ? (
          <div className="break-words font-sans text-sm font-medium leading-5 text-secondary">{account.name}</div>
        ) : (
          <Skeleton className="h-5 w-32" />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {navItems.map((item) => {
          const Icon = NAV_ICONS[item.id];
          return (
            <NavLink
              key={item.id}
              to={item.to}
              active={item.id === 'settings' ? isSettingsPage : isActive(item.to)}
              onClick={handleNavClick}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Quick actions + timer - Log activity and Start timer sit side-by-side
          in the idle state; the timer widget expands when a timer is active.
          Both are timekeeping, so they go with the Activities link above. */}
      {firmOnlySurfacesVisible(navInputs) && <TimerWidget />}
    </aside>
  );
}

function NavLink({
  to,
  active,
  onClick,
  children,
}: {
  to: string;
  active: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const prefetchData = usePrefetchRouteData();
  // Warm the route's code and its first data reads before the user clicks.
  // Hover typically fires 200-400ms before the click; focus covers keyboard
  // nav; touchstart covers mobile. Both are idempotent, so repeat fires from
  // all three handlers don't re-download.
  const prefetch = () => {
    if (active) return;
    const loader = sidebarRouteLoaders[to];
    if (loader) void prefetchChunk(loader);
    prefetchData(to);
  };
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onTouchStart={prefetch}
      className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm no-underline transition-colors ${
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'font-normal text-text-muted hover:bg-bg-muted hover:text-secondary'
      }`}
    >
      {children}
    </Link>
  );
}

const NAV_ICONS: Record<NavItemId, ComponentType<{ className?: string }>> = {
  dashboard: DashboardIcon,
  clients: ClientsIcon,
  activities: ClockIcon,
  expenses: ReceiptIcon,
  invoices: InvoiceIcon,
  emails: MailIcon,
  calendar: CalendarIcon,
  'bank-accounts': BankIcon,
  accounting: BookOpenIcon,
  settings: SettingsIcon,
};

// Simple inline SVG icons
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ClientsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function InvoiceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ReceiptIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14h6m-6-4h6m-7 8l-2 2V6a2 2 0 012-2h8a2 2 0 012 2v14l-2-2-2 2-2-2-2 2-2-2z" />
    </svg>
  );
}

function BankIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V10m4 11V10m6 11V10m4 11V10M2 10l10-6 10 6H2z"/>
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}
